import React, { useEffect, useRef, useState } from "react";
import "../../../Assets/styles/LandingStyles/CopyrightFooter.css";
import PoweredByDefault from '../../../Assets/images/SALogo.png';
import PoweredByBlackText from '../../../Assets/images/SALogoBlackText.png';

const CopyrightFooter: React.FC = () => {
    const sealContainerRef = useRef<HTMLDivElement>(null);
    const [sealStatus, setSealStatus] = useState<"loading" | "loaded" | "error">("loading");
    const [isSmallScreen, setIsSmallScreen] = useState(false);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 970px)');
        const handleMediaChange = (e: MediaQueryListEvent) => {
            setIsSmallScreen(e.matches);
        };

        // Initial check
        setIsSmallScreen(mediaQuery.matches);

        // Add listener
        mediaQuery.addEventListener('change', handleMediaChange);

        // Cleanup
        return () => mediaQuery.removeEventListener('change', handleMediaChange);
    }, []);

    useEffect(() => {
        const loadSeal = () => {
            if (!sealContainerRef.current) return;

            // Create a specific element with the ID that GoDaddy's script expects
            const sealElement = document.createElement("span");
            sealElement.id = "siteseal"; // This is the ID that GoDaddy scripts typically look for

            // Create a container for the seal content
            const sealContentDiv = sealContainerRef.current.querySelector(".seal-content");
            if (sealContentDiv) {
                sealContentDiv.appendChild(sealElement);
            }

            // Now load the script
            const script = document.createElement("script");
            script.src = "https://seal.godaddy.com/getSeal?sealID=vTe5c1dfcXAPJXEQdibfwglLlZpR7wJV6Uz5NUWXbrxx6s0ojR87ggwBZScv";
            script.async = true;
            script.defer = true;
            script.type = "text/javascript";

            const handleLoad = () => {
                // Check if the seal element has content after script loads
                setTimeout(() => {
                    if (sealElement.children.length > 0 || sealElement.innerHTML.trim() !== "") {
                        setSealStatus("loaded");
                    } else {
                        setSealStatus("error");
                    }
                }, 1000); // Give it a second to render
            };

            const handleError = () => setSealStatus("error");
            script.addEventListener("load", handleLoad);
            script.addEventListener("error", handleError);
            document.body.appendChild(script); // Add to body instead of the container

            return () => {
                script.removeEventListener("load", handleLoad);
                script.removeEventListener("error", handleError);
                if (document.body.contains(script)) {
                    document.body.removeChild(script);
                }
                if (sealElement.parentNode) {
                    sealElement.parentNode.removeChild(sealElement);
                }
            };
        };

        // Small delay to ensure component is fully mounted
        const timer = setTimeout(loadSeal, 100);
        return () => clearTimeout(timer);
    }, []);

    return (
        <footer className="footer-copyright" role="contentinfo">
            <div className="footer-content">
                {/* Left: GoDaddy Seal */}
                <div
                    ref={sealContainerRef}
                    className="seal-container"
                    aria-label={sealStatus === "loaded" ? "Security verification seal" : "Security status"}
                    role={sealStatus === "loaded" ? "img" : "status"}
                >
                    {/* Loading indicator */}
                    <div className={`loading-indicator ${sealStatus !== "loading" ? "hidden" : ""}`}>
                        <div className="pulsing-dot" />
                        <span className="loading-text">Verifying security...</span>
                    </div>

                    {/* Error message */}
                    {sealStatus === "error" && (
                        <span className="seal-error" role="alert">Security seal unavailable</span>
                    )}

                    {/* Seal content container */}
                    <div className={`seal-content ${sealStatus === "loaded" ? "seal-visible" : "seal-hidden"}`}>
                        {/* GoDaddy seal will be injected here */}
                    </div>

                    <span id="gd-seal-description" hidden>
                        GoDaddy website security verification seal
                    </span>
                </div>

                {/* Center: Copyright Text */}
                <div className="copyright-text">
                    Copyright © 2025 Software Architects Ltd. All rights reserved.
                </div>

                {/* Right: Powered By Logo */}
                <a
                    className="powered-by-link"
                    href="https://www.softwarearchitectsjm.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <img
                        src={isSmallScreen ? PoweredByBlackText : PoweredByDefault}
                        alt="Powered by Software Architects Limited"
                        className="powered-by-logo"
                    />
                </a>
            </div>
        </footer>
    );
};

export default CopyrightFooter;